<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AppPopover from '@uiKit/elements/Popover.vue'
import { LEGAL_INDEX_PATH } from '@modules/legal'

// Top bar for the authenticated shell. Title + subtitle come from the active
// route's meta (set by the layout); the avatar opens an account menu holding the
// profile link, Policies & Legal and logout. The hamburger opens the sidebar drawer on mobile.
withDefaults(
  defineProps<{
    title: string
    subtitle?: string
    unreadCount?: number
  }>(),
  { subtitle: '', unreadCount: 0 },
)

const emit = defineEmits<{
  'toggle-nav': []
  'open-notifications': []
  logout: []
}>()

const accountMenuRef = ref<InstanceType<typeof AppPopover> | null>(null)

function toggleAccountMenu(event: Event) {
  accountMenuRef.value?.toggle(event)
}

function handleLogout() {
  accountMenuRef.value?.hide()
  emit('logout')
}

// The popover doesn't close on its own when the route changes underneath it, so
// navigating from a menu row has to dismiss it explicitly.
function closeAccountMenu() {
  accountMenuRef.value?.hide()
}
</script>

<template>
  <Box is="header" flex align="center" justify="between" gap="3" class="header">
    <Box flex align="center" gap="2" class="header__left">
      <Box
        is="button"
        align="center"
        justify="center"
        class="header__icon-btn header__mobile-toggle"
        aria-label="Open menu"
        @click="emit('toggle-nav')"
      >
        <PiIcon name="bars" />
      </Box>
      <Box direction="col" gap="1" class="header__titles">
        <Text is="h1" type="title-lg" weight="bold" truncate>{{ title }}</Text>
        <Text v-if="subtitle" type="body-sm" color="muted" truncate>{{ subtitle }}</Text>
      </Box>
    </Box>

    <Box flex align="center" gap="2" class="header__right">
      <Box
        is="button"
        align="center"
        justify="center"
        class="header__icon-btn header__bell"
        aria-label="Notifications"
        @click="emit('open-notifications')"
      >
        <PiIcon name="bell" />
        <Box v-if="unreadCount > 0" class="header__bell-dot" />
      </Box>

      <Box
        is="button"
        align="center"
        justify="center"
        class="header__avatar"
        aria-label="Account menu"
        aria-haspopup="menu"
        @click="toggleAccountMenu"
      >
        <PiIcon name="user" />
      </Box>

      <AppPopover ref="accountMenuRef">
        <Box is="nav" direction="col" class="account-menu" aria-label="Account">
          <RouterLink
            :to="routes.PROFILE"
            class="account-menu__item"
            active-class="account-menu__item--active"
            @click="closeAccountMenu"
          >
            <PiIcon name="user" class="account-menu__icon" />
            <Text is="span" type="body" weight="medium">Profile</Text>
          </RouterLink>

          <!-- New tab: the legal pages are public and render outside the app
               shell, so opening them in place would drop the seller out of the
               portal's navigation. -->
          <RouterLink
            :to="LEGAL_INDEX_PATH"
            target="_blank"
            class="account-menu__item"
            @click="closeAccountMenu"
          >
            <PiIcon name="book" class="account-menu__icon" />
            <Text is="span" type="body" weight="medium">Policies &amp; Legal</Text>
          </RouterLink>

          <Box
            is="button"
            class="account-menu__item account-menu__item--danger"
            @click="handleLogout"
          >
            <PiIcon name="sign-out" class="account-menu__icon" />
            <Text is="span" type="body" weight="medium" color="danger">Log out</Text>
          </Box>
        </Box>
      </AppPopover>
    </Box>
  </Box>
</template>

<style scoped>
.header {
  height: 72px;
  flex-shrink: 0;
  padding: 0 1.5rem;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
}

.header__left {
  min-width: 0;
}

.header__titles {
  min-width: 0;
}

.header__right {
  flex-shrink: 0;
}

.header__icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  color: var(--p-text-muted-color);
  cursor: pointer;
  padding: 0;
  position: relative;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.header__icon-btn:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.header__icon-btn:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.header__bell-dot {
  position: absolute;
  top: 9px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--p-danger-500);
  border: 1.5px solid var(--p-surface-0);
}

.header__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-50);
  color: var(--p-text-color);
  cursor: pointer;
  padding: 0;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.header__avatar:hover {
  background: var(--p-surface-100);
  border-color: var(--p-primary-300);
}

.header__avatar:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.account-menu {
  min-width: 200px;
  padding: 0.375rem;
}

.account-menu__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  border: none;
  background: transparent;
  color: var(--p-text-color);
  cursor: pointer;
  text-align: left;
  padding: 0.625rem 0.75rem;
  border-radius: var(--p-border-radius-sm);
  transition: background 0.15s ease;
}

.account-menu__item:hover {
  background: var(--p-surface-100);
}

.account-menu__item--active {
  background: var(--p-surface-100);
}

.account-menu__item:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

/* Scoped to the icon on purpose: the label sets its own colour via the Text
   `color` prop, which outranks an inherited one. */
.account-menu__item--danger .account-menu__icon {
  color: var(--p-danger-600);
}

.account-menu__icon {
  font-size: 1rem;
}

.header__mobile-toggle {
  display: none;
}

@media (max-width: 991px) {
  .header {
    height: 64px;
    padding: 0 1rem;
  }

  .header__mobile-toggle {
    display: inline-flex;
  }
}
</style>
